import { useEffect, useRef } from 'react';
import { reaction, runInAction } from 'mobx';
import { reactionsStore } from 'state/store';
import {
    MAX_VISIBLE_REACTIONS,
    REACTION_DISPLAY_DURATION,
    REACTION_FADE_OUT_DURATION,
} from 'features/Reactions/consts';
import { Reaction } from '../types';

/**
 * Хук для управления таймерами автоматического удаления реакций.
 * Управляет таймерами для обычного удаления и fade-out при превышении лимита.
 */
export const useReactionsTimers = () => {
    const autoRemoveTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const fadeOutTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const processedReactionsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        // Очистка всех таймеров при размонтировании
        return () => {
            autoRemoveTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
            autoRemoveTimeoutsRef.current.clear();
            fadeOutTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
            fadeOutTimeoutsRef.current.clear();
            processedReactionsRef.current.clear();
        };
    }, []);

    useEffect(() => {
        const setupAutoRemoveTimer = (reaction: Reaction) => {
            // Очищаем существующий таймер, если есть
            const existingTimeout = autoRemoveTimeoutsRef.current.get(reaction.id);
            if (existingTimeout) {
                clearTimeout(existingTimeout);
            }

            // Устанавливаем таймер на удаление
            const timeout = setTimeout(() => {
                if (document.hidden) return;
                runInAction(() => {
                    reactionsStore.removeReaction(reaction.id);
                });
                autoRemoveTimeoutsRef.current.delete(reaction.id);
                processedReactionsRef.current.delete(reaction.id);
            }, REACTION_DISPLAY_DURATION);
            autoRemoveTimeoutsRef.current.set(reaction.id, timeout);
        };

        const setupFadeOutTimer = (reactionId: string) => {
            // Очищаем обычный таймер
            const autoTimeout = autoRemoveTimeoutsRef.current.get(reactionId);
            if (autoTimeout) {
                clearTimeout(autoTimeout);
                autoRemoveTimeoutsRef.current.delete(reactionId);
            }

            // Устанавливаем таймер на удаление через 400ms
            const fadeTimeout = setTimeout(() => {
                if (document.hidden) return;
                runInAction(() => {
                    reactionsStore.removeReaction(reactionId);
                });
                fadeOutTimeoutsRef.current.delete(reactionId);
                processedReactionsRef.current.delete(reactionId);
            }, REACTION_FADE_OUT_DURATION);
            fadeOutTimeoutsRef.current.set(reactionId, fadeTimeout);
        };

        // Подписываемся на изменения реакций
        const dispose = reaction(
            () => reactionsStore.reactions,
            (reactions) => {
                // Игнорируем если вкладка скрыта
                if (document.hidden) return;

                // Находим новые реакции (которых еще нет в processed)
                const newReactions = reactions.filter((r) => !processedReactionsRef.current.has(r.id));

                // Устанавливаем таймеры для новых реакций
                newReactions.forEach((r) => {
                    processedReactionsRef.current.add(r.id);
                    setupAutoRemoveTimer(r);
                });

                // Если превышен лимит, помечаем старые реакции для исчезновения
                if (reactions.length > MAX_VISIBLE_REACTIONS) {
                    const nonFadingReactions = reactions.filter((r) => !r.isFading);
                    const excessCount = nonFadingReactions.length - MAX_VISIBLE_REACTIONS;
                    const reactionsToFade = nonFadingReactions.slice(0, excessCount);

                    reactionsToFade.forEach((r) => {
                        // Помечаем как исчезающую
                        runInAction(() => {
                            reactionsStore.markReactionAsFading(r.id);
                        });

                        setupFadeOutTimer(r.id);
                    });
                }
            },
        );

        return dispose;
    }, []);
};
